import { Injectable } from '@angular/core'
import { getDaysInPeriod, getFirstDayOfUTCMonth, getYearMonthString } from '../utils/payroll.utils'
import {
  EmployeeSalary,
  ExtendedSalaryComponent
} from '../store/employee-salary/employee-salary.state'
import {
  PayrollCalculationBreakdown,
  PayrollCalculationContext,
  PayrollCalculationResult,
  SalaryComponent
} from '../store/payroll/payroll.state'

@Injectable({ providedIn: 'root' })
export class PayrollCalculationService {
  private monthlyTdsThreshold = 106250 // According to new tax regime in INR

  private round(value: number): number {
    return parseFloat(value.toFixed(2))
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // MAIN ORCHESTRATOR
  // ─────────────────────────────────────────────────────────────────────────────
  public calculatePayrollMetrics(ctx: PayrollCalculationContext): PayrollCalculationResult {
    const {
      currentSalary,
      previousSalary,
      payPeriodStart,
      payPeriodEnd,
      lopDays,
      lopDaysForArrearsPeriod,
      bonus,
      expenses,
      adjustments,
      rounding
    } = ctx

    const payPeriodStartDateObj = new Date(payPeriodStart + 'T00:00:00Z')
    const payPeriodEndDateObj = new Date(payPeriodEnd + 'T00:00:00Z')
    const daysInCurrentPayPeriod = getDaysInPeriod(payPeriodStartDateObj, payPeriodEndDateObj)

    const currentSalaryEffectiveFromObj = new Date(currentSalary.effectiveFrom + 'T00:00:00Z')
    const firstDayOfCurrentPayPeriodMonth = getFirstDayOfUTCMonth(payPeriodStartDateObj)
    const payrollMonthYearStr = getYearMonthString(payPeriodStartDateObj)

    const isArrearsCase =
      !!previousSalary &&
      currentSalary.payoutFrom === payrollMonthYearStr &&
      currentSalaryEffectiveFromObj < firstDayOfCurrentPayPeriodMonth

    const isProrationCase =
      !!previousSalary &&
      currentSalary.payoutFrom === payrollMonthYearStr &&
      currentSalaryEffectiveFromObj > firstDayOfCurrentPayPeriodMonth &&
      currentSalaryEffectiveFromObj <= payPeriodEndDateObj

    let calculatedArrearsAmount = 0
    let breakdown: PayrollCalculationBreakdown = { earnings: [], deductions: [] }

    const lopProrationFactor =
      daysInCurrentPayPeriod > 0 ? (daysInCurrentPayPeriod - lopDays) / daysInCurrentPayPeriod : 0

    if (isArrearsCase && previousSalary) {
      calculatedArrearsAmount = this.calculateArrears(
        currentSalary,
        previousSalary,
        firstDayOfCurrentPayPeriodMonth,
        lopDaysForArrearsPeriod
      )
      breakdown = this.calculateMonthlySalaryComponents(
        currentSalary,
        lopProrationFactor,
        payPeriodStart,
        payPeriodEnd
      )
    } else if (isProrationCase && previousSalary) {
      breakdown = this.calculateProratedSalary(
        currentSalary,
        previousSalary,
        payPeriodStartDateObj,
        payPeriodEndDateObj,
        lopProrationFactor
      )
    } else {
      breakdown = this.calculateMonthlySalaryComponents(
        currentSalary,
        lopProrationFactor,
        payPeriodStart,
        payPeriodEnd,
        ctx
      )
    }

    // Contextual earnings
    if (calculatedArrearsAmount > 0) {
      breakdown.earnings.push({ name: 'Arrears', amount: this.round(calculatedArrearsAmount) })
    }
    if (bonus > 0) {
      breakdown.earnings.push({ name: 'Bonus', amount: this.round(bonus) })
    }
    if (expenses > 0) {
      breakdown.earnings.push({ name: 'Expenses Reimbursed', amount: this.round(expenses) })
    }
    if (adjustments !== 0) {
      if (adjustments > 0) {
        breakdown.earnings.push({ name: 'Other Adjustments', amount: this.round(adjustments) })
      } else {
        // breakdown.deductions.push({
        //   name: 'Other Adjustments',
        //   amount: this.round(Math.abs(adjustments))
        // })
        this.upsertBreakdownItem(
          breakdown.deductions,
          'Other Adjustments',
          this.round(Math.abs(adjustments))
        )
      }
    }

    const grossFromSalary = this.round(
      breakdown.earnings.reduce((sum, comp) => sum + comp.amount, 0)
    )
    const tdsAfterCess = this.calculateTds(grossFromSalary)
    if (tdsAfterCess > 0) {
      breakdown.deductions.push({ name: 'TDS (Tax Deducted at Source)', amount: tdsAfterCess })
    }

    if (rounding !== 0) {
      if (rounding > 0) {
        breakdown.earnings.push({ name: 'Rounding Adjustment', amount: this.round(rounding) })
      } else {
        breakdown.deductions.push({
          name: 'Rounding Adjustment',
          amount: this.round(Math.abs(rounding))
        })
      }
    }

    const finalGrossSalary = this.round(
      breakdown.earnings.reduce((sum, comp) => sum + comp.amount, 0)
    )

    const payPeriodStartDate = new Date(payPeriodStart)
    const esiComponent = currentSalary.salaryData.otherComponents?.find(comp => comp.name === 'ESI')
    if (
      finalGrossSalary >= 0 &&
      finalGrossSalary <= 21000 &&
      payPeriodStartDate >= new Date('2025-11-01') &&
      esiComponent &&
      esiComponent.info.isInCTC === false
    ) {
      const esiAmount = this.round(finalGrossSalary * 0.0075)
      breakdown.deductions.push({ name: 'ESI', amount: esiAmount })
    }

    const finalTotalDeductions = this.round(
      breakdown.deductions.reduce((sum, comp) => sum + comp.amount, 0)
    )
    const netSalary = this.round(finalGrossSalary - finalTotalDeductions)

    return {
      grossSalary: Math.max(0, finalGrossSalary),
      netSalary,
      tdsAfterCess,
      arrearsAmountDisplay: calculatedArrearsAmount,
      calculationBreakdown: breakdown
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // GROSS METRICS (used for GPS / joining-date-aware calculation)
  // ─────────────────────────────────────────────────────────────────────────────
  // before 25 may 2026
  // public calculateGrossMetrics(
  //   ctx: PayrollCalculationContext,
  //   employeeJoiningDate: string | Date | null,
  //   salaryHoldFroms: string | Date | null | undefined,
  //   salaryHoldTos: string | Date | null | undefined,
  //   salaryHold: boolean
  // ): {
  //   grossSalary: number
  //   calculationBreakdown: PayrollCalculationBreakdown
  //   netSalary: number
  // } {
  //   const {
  //     currentSalary,
  //     previousSalary,
  //     payPeriodStart,
  //     payPeriodEnd,
  //     lopDays,
  //     lopDaysForArrearsPeriod,
  //     bonus,
  //     expenses,
  //     adjustments,
  //     tdsAfterCess,
  //     salaryHoldFrom,
  //     salaryHoldTo
  //   } = ctx

  //   // ── Pay period start: respect joining date ──────────────────────────────
  //   let gpsPayPeriodStart: any = payPeriodStart
  //   if (employeeJoiningDate) {
  //     gpsPayPeriodStart =
  //       employeeJoiningDate >= payPeriodStart ? employeeJoiningDate : payPeriodStart
  //   }

  //   const payPeriodStartDateObj = new Date(gpsPayPeriodStart + 'T00:00:00Z')
  //   const payPeriodEndDateObj = new Date(payPeriodEnd + 'T00:00:00Z')
  //   const daysInCurrentPayPeriod = getDaysInPeriod(payPeriodStartDateObj, payPeriodEndDateObj)

  //   const currentSalaryEffectiveFromObj = new Date(currentSalary.effectiveFrom + 'T00:00:00Z')
  //   const firstDayOfCurrentPayPeriodMonth = getFirstDayOfUTCMonth(payPeriodStartDateObj)
  //   const payrollMonthYearStr = getYearMonthString(payPeriodStartDateObj)

  //   // ── Salary hold logic ───────────────────────────────────────────────────
  //   const holdFromStr = salaryHoldFrom ?? salaryHoldFroms ?? null
  //   const holdToStr = salaryHoldTo ?? salaryHoldTos ?? null
  //   const holdFromDate = holdFromStr ? new Date(holdFromStr) : null
  //   const holdToDate = holdToStr ? new Date(holdToStr) : null

  //   let workedDays = daysInCurrentPayPeriod
  //   if (holdFromDate && holdToDate && salaryHold) {
  //     const daysBeforeHold = holdFromDate.getDate() - payPeriodStartDateObj.getDate()
  //     workedDays = Math.max(0, daysBeforeHold)
  //   }

  //   let lopProrationFactor = 0
  //   if (salaryHold && (salaryHoldFrom || salaryHoldFroms)) {
  //     workedDays = Math.max(0, Math.min(workedDays, daysInCurrentPayPeriod))
  //     lopProrationFactor =
  //       daysInCurrentPayPeriod > 0 ? (workedDays - lopDays) / daysInCurrentPayPeriod : 0
  //   } else {
  //     lopProrationFactor =
  //       daysInCurrentPayPeriod > 0 ? (daysInCurrentPayPeriod - lopDays) / daysInCurrentPayPeriod : 0
  //   }

  //   // ── Arrears / Proration detection ───────────────────────────────────────
  //   const isArrearsCase =
  //     !!previousSalary &&
  //     currentSalary.payoutFrom === payrollMonthYearStr &&
  //     currentSalaryEffectiveFromObj < firstDayOfCurrentPayPeriodMonth

  //   const isProrationCase =
  //     !!previousSalary &&
  //     currentSalary.payoutFrom === payrollMonthYearStr &&
  //     currentSalaryEffectiveFromObj > firstDayOfCurrentPayPeriodMonth &&
  //     currentSalaryEffectiveFromObj <= payPeriodEndDateObj

  //   let calculatedArrearsAmount = 0
  //   let breakdown: PayrollCalculationBreakdown = { earnings: [], deductions: [] }

  //   if (isArrearsCase && previousSalary) {
  //     calculatedArrearsAmount = this.calculateArrears(
  //       currentSalary,
  //       previousSalary,
  //       firstDayOfCurrentPayPeriodMonth,
  //       lopDaysForArrearsPeriod
  //     )
  //     breakdown = this.calculateMonthlySalaryComponents(
  //       currentSalary,
  //       lopProrationFactor,
  //       payPeriodStart,
  //       payPeriodEnd
  //     )
  //   } else if (isProrationCase && previousSalary) {
  //     breakdown = this.calculateProratedSalary(
  //       currentSalary,
  //       previousSalary,
  //       payPeriodStartDateObj,
  //       payPeriodEndDateObj,
  //       lopProrationFactor
  //     )
  //   } else {
  //     breakdown = this.calculateMonthlySalaryComponents(
  //       currentSalary,
  //       lopProrationFactor,
  //       payPeriodStart,
  //       payPeriodEnd
  //     )
  //   }

  //   // ── Contextual earnings ─────────────────────────────────────────────────
  //   if (calculatedArrearsAmount > 0) {
  //     breakdown.earnings.push({ name: 'Arrears', amount: this.round(calculatedArrearsAmount) })
  //   }
  //   if (bonus > 0) {
  //     breakdown.earnings.push({ name: 'Bonus', amount: this.round(bonus) })
  //   }
  //   if (expenses > 0) {
  //     breakdown.earnings.push({ name: 'Expenses Reimbursed', amount: this.round(expenses) })
  //   }
  //   if (adjustments !== 0) {
  //     if (adjustments > 0) {
  //       breakdown.earnings.push({ name: 'Other Adjustments', amount: this.round(adjustments) })
  //     } else {
  //       breakdown.deductions.push({
  //         name: 'Other Adjustments',
  //         amount: this.round(Math.abs(adjustments))
  //       })
  //     }
  //   }

  //   // ── Rounding ────────────────────────────────────────────────────────────
  //   if (ctx.rounding !== 0) {
  //     if (ctx.rounding > 0) {
  //       breakdown.earnings.push({ name: 'Rounding Adjustment', amount: this.round(ctx.rounding) })
  //     } else {
  //       breakdown.deductions.push({
  //         name: 'Rounding Adjustment',
  //         amount: this.round(Math.abs(ctx.rounding))
  //       })
  //     }
  //   }
  //   // console.log('break down earnings', breakdown.earnings)
  //   // ── Final gross (before statutory deductions) ───────────────────────────
  //   const finalGrossSalary = this.round(
  //     breakdown.earnings.reduce((sum, comp) => sum + comp.amount, 0)
  //   )

  //   // ── ESI eligibility dates ───────────────────────────────────────────────
  //   const payPeriodStartDate = new Date(payPeriodStart)
  //   payPeriodStartDate.setHours(0, 0, 0, 0)

  //   const esiStartDate = new Date(2025, 10, 1) // 1-Nov-2025
  //   esiStartDate.setHours(0, 0, 0, 0)

  //   // ── Statutory / Custom Deductions from otherComponents ──────────────────
  //   // BUG FIX: ESI isInCTC check added.
  //   //   - If ESI isInCTC === true  → it is already part of gross salary components
  //   //     computed in calculateMonthlySalaryComponents, so we only add it as a
  //   //     DEDUCTION (employee share) without adding to earnings again.
  //   //   - If ESI isInCTC === false → it is NOT in gross; employer contribution is
  //   //     an extra cost and employee contribution is still a deduction from net.
  //   //
  //   // For all other customDeductions the same CTC-aware logic applies.

  //   const otherComponents = ctx.currentSalary.salaryData.otherComponents || []

  //   for (const component of otherComponents) {
  //     if (component.componentType !== 'customDeductions') continue

  //     const info = component.info || {}
  //     const isInCTC = info.isInCTC === true

  //     // ── Calculate raw employee & employer contributions ──────────────────
  //     let employeeContribution = 0
  //     let employerContribution = 0

  //     if (info.calculationType === 'percentage') {
  //       employeeContribution = this.round(
  //         finalGrossSalary * ((info.employeeContribution || 0) / 100)
  //       )
  //       employerContribution = this.round(
  //         finalGrossSalary * ((info.employerContribution || 0) / 100)
  //       )
  //     } else {
  //       // Fixed amount
  //       employeeContribution = this.round(Number(info.employeeContribution || 0))
  //       employerContribution = this.round(Number(info.employerContribution || 0))
  //     }

  //     // ── ESI special handling ─────────────────────────────────────────────
  //     // if (component.name === 'ESI' || component.name === 'ESI Old') {
  //     //   // Check statutory flag & eligibility thresholds
  //     //   const esiStatutoryEnabled = ctx.currentSalary.statutoryCompliances?.esi === true
  //     //   const esiEligible =
  //     //     esiStatutoryEnabled && finalGrossSalary <= 21000 && payPeriodStartDate >= esiStartDate

  //     //   if (!esiEligible) {
  //     //     // Not eligible → zero out both contributions, skip this component
  //     //     continue
  //     //   }

  //     //   if (isInCTC) {
  //     //     // ESI is inside CTC:
  //     //     //   • Employer contribution is already baked into gross components
  //     //     //     (calculateMonthlySalaryComponents handled it via otherComponents loop)
  //     //     //   • We only need to deduct the EMPLOYEE share from net
  //     //     //   • Do NOT add employer contribution to earnings here (already there)
  //     //     if (employeeContribution > 0) {
  //     //       breakdown.deductions.push({
  //     //         name: 'ESI (Employee Contribution)',
  //     //         amount: employeeContribution
  //     //       })
  //     //     }
  //     //     if (employerContribution > 0) {
  //     //       breakdown.deductions.push({
  //     //         name: 'ESI (Employer Contribution)',
  //     //         amount: employerContribution
  //     //       })
  //     //     }
  //     //     // Employer contribution: already in CTC gross, no extra action needed
  //     //   }
  //     //   else {
  //     //     // ESI is outside CTC:
  //     //     //   • Employer contribution is an ADDITIONAL cost (not in gross)
  //     //     //   • Employee contribution is deducted from net
  //     //     if (employeeContribution > 0) {
  //     //       breakdown.deductions.push({
  //     //         name: 'ESI (Employee Contribution)',
  //     //         amount: employeeContribution
  //     //       })
  //     //     }
  //     //     // Employer contribution is recorded separately (not added to employee earnings)
  //     //     // You can emit this to a separate employer cost tracker if needed.
  //     //   }

  //     //   continue // ESI fully handled — skip generic block below
  //     // }

  //     // ── Generic customDeductions handling (non-ESI) ──────────────────────
  //     // if (employeeContribution > 0) {
  //     //   breakdown.deductions.push({
  //     //     name: component.name,
  //     //     amount: employeeContribution
  //     //   })
  //     // }

  //     // Employer contribution: only add to earnings if it is NOT already in CTC
  //     // (if isInCTC, it was already included in calculateMonthlySalaryComponents)
  //     // if (employerContribution > 0 && !isInCTC) {
  //     //   // This is an extra employer cost — do NOT add to employee's earnings/gross.
  //     //   // Track externally if needed; do not push to breakdown.earnings here.
  //     // }
  //   }

  //   // ── Net salary ──────────────────────────────────────────────────────────
  //   // BUG FIX: netSalary was referenced before declaration in original code.
  //   const finalTotalDeductions = this.round(
  //     breakdown.deductions.reduce((sum, comp) => sum + comp.amount, 0)
  //   )
  //   // tdsAfterCess comes from ctx (already calculated upstream) — add to deductions if > 0
  //   const tdsDeduction = tdsAfterCess && tdsAfterCess > 0 ? tdsAfterCess : 0
  //   const netSalary = this.round(finalGrossSalary - finalTotalDeductions - tdsDeduction)

  //   return {
  //     grossSalary: Math.max(0, finalGrossSalary),
  //     calculationBreakdown: breakdown,
  //     netSalary: Math.max(0, netSalary)
  //   }
  // }

  // ─────────────────────────────────────────────────────────────────────────────
  // ARREARS
  // ─────────────────────────────────────────────────────────────────────────────

  //25 may 2026 gps
  // public calculateGrossMetrics(
  //   ctx: PayrollCalculationContext,
  //   employeeJoiningDate: string | Date | null,
  //   salaryHoldFroms: string | Date | null | undefined,
  //   salaryHoldTos: string | Date | null | undefined,
  //   salaryHold: boolean
  // ): {
  //   grossSalary: number
  //   calculationBreakdown: PayrollCalculationBreakdown
  //   netSalary: number
  // } {
  //   const {
  //     currentSalary,
  //     previousSalary,
  //     payPeriodStart,
  //     payPeriodEnd,
  //     // lopDays,
  //     lopDaysForArrearsPeriod,
  //     bonus,
  //     expenses,
  //     adjustments,
  //     tdsAfterCess,
  //     salaryHoldFrom,
  //     salaryHoldTo
  //   } = ctx
  //   let lopDays = ctx.lopDays
  //   // ────────────────────────────────────────────────────────────────────────
  //   // Pay period start
  //   // ────────────────────────────────────────────────────────────────────────

  //   let gpsPayPeriodStart: any = payPeriodStart

  //   if (employeeJoiningDate) {
  //     gpsPayPeriodStart =
  //       employeeJoiningDate >= payPeriodStart ? employeeJoiningDate : payPeriodStart
  //   }

  //   const payPeriodStartDateObj = new Date(gpsPayPeriodStart + 'T00:00:00Z')

  //   const payPeriodEndDateObj = new Date(payPeriodEnd + 'T00:00:00Z')

  //   const daysInCurrentPayPeriod = getDaysInPeriod(payPeriodStartDateObj, payPeriodEndDateObj)
  //   // const daysInCurrentPayPeriod = getDaysInPeriod(gpsPayPeriodStart, payPeriodEndDateObj)

  //   const currentSalaryEffectiveFromObj = new Date(currentSalary.effectiveFrom + 'T00:00:00Z')

  //   const firstDayOfCurrentPayPeriodMonth = getFirstDayOfUTCMonth(payPeriodStartDateObj)

  //   const payrollMonthYearStr = getYearMonthString(payPeriodStartDateObj)

  //   // ────────────────────────────────────────────────────────────────────────
  //   // Salary hold logic
  //   // ────────────────────────────────────────────────────────────────────────

  //   const holdFromStr = salaryHoldFrom ?? salaryHoldFroms ?? null

  //   const holdToStr = salaryHoldTo ?? salaryHoldTos ?? null

  //   const holdFromDate = holdFromStr ? new Date(holdFromStr) : null

  //   const holdToDate = holdToStr ? new Date(holdToStr) : null

  //   let workedDays = daysInCurrentPayPeriod
  //   // console.log('daysInCurrentPayPeriod', daysInCurrentPayPeriod)
  //   if (holdFromDate && holdToDate && salaryHold) {
  //     const daysBeforeHold = holdFromDate.getDate() - payPeriodStartDateObj.getDate()

  //     workedDays = Math.max(0, daysBeforeHold)
  //   }

  //   let lopProrationFactor = 0

  //   if (salaryHold && (salaryHoldFrom || salaryHoldFroms)) {
  //     workedDays = Math.max(0, Math.min(workedDays, daysInCurrentPayPeriod))

  //     lopProrationFactor =
  //       daysInCurrentPayPeriod > 0 ? (workedDays - lopDays) / daysInCurrentPayPeriod : 0
  //   } else {
  //     lopProrationFactor =
  //       daysInCurrentPayPeriod > 0 ? (daysInCurrentPayPeriod - lopDays) / daysInCurrentPayPeriod : 0
  //   }

  //   const effectiveFromObj = new Date(currentSalary.effectiveFrom + 'T00:00:00Z')

  //   const monthStart = firstDayOfCurrentPayPeriodMonth
  //   const monthEnd = payPeriodEndDateObj
  //   const diffDays = getDaysInPeriod(monthStart, monthEnd)
  //   // let salaryStartDate = monthStart

  //   if (effectiveFromObj > monthStart) {
  //     // salaryStartDate = effectiveFromObj

  //     // const payableDays = getDaysInPeriod(salaryStartDate, payPeriodEndDateObj)
  //     // 👇 THIS is what you were missing
  //     // const unpaidDays = getDaysInPeriod(monthStart, salaryStartDate)
  //     // let lopDays = lopDays
  //     lopDays += diffDays - daysInCurrentPayPeriod
  //     lopProrationFactor = daysInCurrentPayPeriod > 0 ? (diffDays - lopDays) / diffDays : 0
  //   }

  //   // ────────────────────────────────────────────────────────────────────────
  //   // Arrears / Proration
  //   // ────────────────────────────────────────────────────────────────────────

  //   const isArrearsCase =
  //     !!previousSalary &&
  //     currentSalary.payoutFrom === payrollMonthYearStr &&
  //     currentSalaryEffectiveFromObj < firstDayOfCurrentPayPeriodMonth

  //   const isProrationCase =
  //     !!previousSalary &&
  //     currentSalary.payoutFrom === payrollMonthYearStr &&
  //     currentSalaryEffectiveFromObj > firstDayOfCurrentPayPeriodMonth &&
  //     currentSalaryEffectiveFromObj <= payPeriodEndDateObj

  //   let calculatedArrearsAmount = 0

  //   let breakdown: PayrollCalculationBreakdown = {
  //     earnings: [],
  //     deductions: []
  //   }
  //   // console.log('isProrationCase', isProrationCase)
  //   // console.log('previousSalary', previousSalary)
  //   // console.log('currentSalary.payoutFrom', currentSalary.payoutFrom)
  //   // console.log('currentSalaryEffectiveFromObj', currentSalaryEffectiveFromObj)
  //   if (isArrearsCase && previousSalary) {
  //     calculatedArrearsAmount = this.calculateArrears(
  //       currentSalary,
  //       previousSalary,
  //       firstDayOfCurrentPayPeriodMonth,
  //       lopDaysForArrearsPeriod
  //     )

  //     breakdown = this.calculateMonthlySalaryComponents(
  //       currentSalary,
  //       lopProrationFactor,
  //       payPeriodStart,
  //       payPeriodEnd
  //     )
  //   } else if (isProrationCase && previousSalary) {
  //     breakdown = this.calculateProratedSalary(
  //       currentSalary,
  //       previousSalary,
  //       payPeriodStartDateObj,
  //       payPeriodEndDateObj,
  //       lopProrationFactor
  //     )
  //   } else {
  //     breakdown = this.calculateMonthlySalaryComponents(
  //       currentSalary,
  //       lopProrationFactor,
  //       payPeriodStart,
  //       payPeriodEnd,
  //       ctx
  //     )
  //   }
  //   console.log('breakdown', breakdown)
  //   // ────────────────────────────────────────────────────────────────────────
  //   // Contextual earnings
  //   // ────────────────────────────────────────────────────────────────────────

  //   if (calculatedArrearsAmount > 0) {
  //     breakdown.earnings.push({
  //       name: 'Arrears',
  //       amount: this.round(calculatedArrearsAmount)
  //     })
  //   }

  //   if (bonus > 0) {
  //     breakdown.earnings.push({
  //       name: 'Bonus',
  //       amount: this.round(bonus)
  //     })
  //   }

  //   if (expenses > 0) {
  //     breakdown.earnings.push({
  //       name: 'Expenses Reimbursed',
  //       amount: this.round(expenses)
  //     })
  //   }

  //   if (adjustments !== 0) {
  //     if (adjustments > 0) {
  //       breakdown.earnings.push({
  //         name: 'Other Adjustments',
  //         amount: this.round(adjustments)
  //       })
  //     } else {
  //       breakdown.deductions.push({
  //         name: 'Other Adjustments',
  //         amount: this.round(Math.abs(adjustments))
  //       })
  //     }
  //   }

  //   // ────────────────────────────────────────────────────────────────────────
  //   // Rounding
  //   // ────────────────────────────────────────────────────────────────────────

  //   if (ctx.rounding !== 0) {
  //     if (ctx.rounding > 0) {
  //       breakdown.earnings.push({
  //         name: 'Rounding Adjustment',
  //         amount: this.round(ctx.rounding)
  //       })
  //     } else {
  //       breakdown.deductions.push({
  //         name: 'Rounding Adjustment',
  //         amount: this.round(Math.abs(ctx.rounding))
  //       })
  //     }
  //   }

  //   // ────────────────────────────────────────────────────────────────────────
  //   // Initial gross
  //   // ────────────────────────────────────────────────────────────────────────

  //   let initialGrossSalary = this.round(
  //     breakdown.earnings.reduce((sum, comp) => sum + comp.amount, 0)
  //   )
  //   // let earningsForGross = breakdown.earnings

  //   if (bonus > 0 && lopDays === 0) {
  //     const otherAllowances = this.round(
  //       breakdown.earnings
  //         .filter(comp => {
  //           const name = comp.name?.toLowerCase()

  //           return !['basic', 'hra', 'special allowances'].includes(name)
  //         })
  //         .reduce((sum, comp) => sum + comp.amount, 0)
  //     )

  //     const specialAllowance = breakdown.earnings.find(
  //       comp => comp.name?.toLowerCase() === 'special allowances'
  //     )

  //     if (specialAllowance) {
  //       specialAllowance.amount = Math.floor(
  //         Math.ceil(specialAllowance.amount) - Math.ceil(otherAllowances) + bonus - 1
  //       )
  //     }
  //     // console.log('special allowances', specialAllowance)
  //   }

  //   // console.log('initial gross salary 651', initialGrossSalary)
  //   // ────────────────────────────────────────────────────────────────────────
  //   // Reverse gross calculation for employer contributions inside CTC
  //   // ────────────────────────────────────────────────────────────────────────

  //   const otherComponents = ctx.currentSalary.salaryData.otherComponents || []

  //   let totalEmployerPercentageInCTC = 0

  //   for (const component of otherComponents) {
  //     if (component.componentType !== 'customDeductions') {
  //       continue
  //     }

  //     const info = component.info || {}

  //     const isInCTC = info.isInCTC === true

  //     if (isInCTC && info.calculationType === 'percentage') {
  //       totalEmployerPercentageInCTC += Number(info.employerContribution || 0)
  //     }
  //   }

  //   const employerMultiplier = 1 + totalEmployerPercentageInCTC / 100

  //   // Actual employee gross excluding employer burden
  //   // const actualEmployeeGross = this.round(initialGrossSalary / employerMultiplier)
  //   const actualEmployeeGross = this.round(currentSalary.ctcValue / employerMultiplier)
  //   const actualEmpMonthlyGross =
  //     !isProrationCase &&
  //     !previousSalary &&
  //     currentSalary.payoutFrom === payrollMonthYearStr &&
  //     currentSalaryEffectiveFromObj > firstDayOfCurrentPayPeriodMonth &&
  //     currentSalaryEffectiveFromObj <= payPeriodEndDateObj
  //       ? initialGrossSalary
  //       : actualEmployeeGross / 12
  //   // Employer burden amount
  //   // const totalEmployerBurden = this.round(initialGrossSalary - actualEmployeeGross)

  //   // ────────────────────────────────────────────────────────────────────────
  //   // Statutory bases
  //   // ────────────────────────────────────────────────────────────────────────

  //   // ESI:
  //   // Include bonus
  //   // Exclude reimbursements / rounding / adjustments

  //   const esiEligibleGross = this.round(
  //     breakdown.earnings
  //       .filter(comp => {
  //         const excluded = ['Expenses Reimbursed', 'Rounding Adjustment', 'Other Adjustments']
  //         // console.log('comp esi', comp)
  //         return !excluded.includes(comp.name)
  //       })
  //       .reduce((sum, comp) => sum + comp.amount, 0) / employerMultiplier
  //   )
  //   // console.log('esiEligibleGross', esiEligibleGross)
  //   // PF:
  //   // Usually Basic + DA only

  //   const pfEligibleGross = this.round(
  //     breakdown.earnings
  //       .filter(comp => {
  //         const allowed = ['Basic', 'DA']

  //         return allowed.includes(comp.name)
  //       })
  //       .reduce((sum, comp) => sum + comp.amount, 0)
  //   )

  //   // ────────────────────────────────────────────────────────────────────────
  //   // ESI dates
  //   // ────────────────────────────────────────────────────────────────────────

  //   const payPeriodStartDate = new Date(payPeriodStart)

  //   payPeriodStartDate.setHours(0, 0, 0, 0)

  //   const esiStartDate = new Date(2025, 10, 1)

  //   esiStartDate.setHours(0, 0, 0, 0)

  //   // ────────────────────────────────────────────────────────────────────────
  //   // Deductions
  //   // ────────────────────────────────────────────────────────────────────────

  //   for (const component of otherComponents) {
  //     if (component.componentType !== 'customDeductions') {
  //       continue
  //     }

  //     const info = component.info || {}

  //     let employeeContribution = 0
  //     let employerContribution = 0

  //     // let calculationBase = actualEmployeeGross
  //     let calculationBase = actualEmpMonthlyGross
  //     // console.log('calculation base', calculationBase)
  //     // ESI
  //     // if (component.name === 'ESI' || component.name === 'ESI Old') {
  //     //   calculationBase = esiEligibleGross
  //     // }

  //     // // PF
  //     // else if (component.name === 'PF' || component.name === 'Provident Fund') {
  //     //   calculationBase = pfEligibleGross
  //     // }

  //     // ────────────────────────────────────────────────────────────────────
  //     // Calculate contributions
  //     // ────────────────────────────────────────────────────────────────────

  //     if (info.calculationType === 'percentage') {
  //       employeeContribution = this.round(
  //         calculationBase * ((info.employeeContribution || 0) / 100)
  //       )

  //       employerContribution = this.round(
  //         calculationBase * ((info.employerContribution || 0) / 100)
  //       )
  //     } else {
  //       employeeContribution = this.round(Number(info.employeeContribution || 0))

  //       employerContribution = this.round(Number(info.employerContribution || 0))
  //     }

  //     // ────────────────────────────────────────────────────────────────────
  //     // ESI handling
  //     // ────────────────────────────────────────────────────────────────────

  //     // if (component.name === 'ESI' || component.name === 'ESI Old') {
  //     if (component.name.toLowerCase().startsWith('esi')) {
  //       const esiStatutoryEnabled = ctx.currentSalary.statutoryCompliances?.esi === true

  //       const esiEligible =
  //         esiStatutoryEnabled && esiEligibleGross <= 21000 && payPeriodStartDate >= esiStartDate

  //       if (!esiEligible) {
  //         continue
  //       }

  //       if (employeeContribution > 0) {
  //         // breakdown.deductions.push({
  //         //   name: 'ESI (Employee Contribution)',
  //         //   amount: employeeContribution
  //         // })
  //         this.upsertBreakdownItem(
  //           breakdown.deductions,
  //           'ESI (Employee Contribution)',
  //           employeeContribution
  //         )
  //       }

  //       // if (employerContribution > 0) {
  //       //   // breakdown.deductions.push({
  //       //   //   name: 'ESI (Employer Contribution)',
  //       //   //   amount: employerContribution
  //       //   // })
  //       //   this.upsertBreakdownItem(
  //       //     breakdown.deductions,
  //       //     'ESI (Employer Contribution)',
  //       //     employeeContribution
  //       //   )
  //       // }

  //       continue
  //     }

  //     // ────────────────────────────────────────────────────────────────────
  //     // PF handling
  //     // ────────────────────────────────────────────────────────────────────

  //     if (component.name === 'PF' || component.name === 'Provident Fund') {
  //       if (employeeContribution > 0) {
  //         breakdown.deductions.push({
  //           name: 'PF (Employee Contribution)',
  //           amount: employeeContribution
  //         })
  //       }

  //       if (employerContribution > 0) {
  //         breakdown.deductions.push({
  //           name: 'PF (Employer Contribution)',
  //           amount: employerContribution
  //         })
  //       }

  //       continue
  //     }

  //     // ────────────────────────────────────────────────────────────────────
  //     // Generic custom deductions
  //     // ────────────────────────────────────────────────────────────────────

  //     if (employeeContribution > 0) {
  //       breakdown.deductions.push({
  //         name: component.name,
  //         amount: employeeContribution
  //       })
  //     }
  //   }

  //   // ────────────────────────────────────────────────────────────────────────
  //   // Final deductions
  //   // ────────────────────────────────────────────────────────────────────────

  //   const finalTotalDeductions = this.round(
  //     breakdown.deductions.reduce((sum, comp) => sum + comp.amount, 0)
  //   )

  //   const tdsDeduction = tdsAfterCess && tdsAfterCess > 0 ? tdsAfterCess : 0

  //   // ────────────────────────────────────────────────────────────────────────
  //   // Net salary
  //   // ────────────────────────────────────────────────────────────────────────

  //   const netSalary = this.round(actualEmployeeGross - finalTotalDeductions - tdsDeduction)

  //   // ────────────────────────────────────────────────────────────────────────
  //   // Return
  //   // ────────────────────────────────────────────────────────────────────────
  //   const esiDeduction = breakdown.deductions.find(comp =>
  //     comp.name?.toLowerCase().startsWith('esi')
  //   )

  //   if (esiDeduction) {
  //     // console.log('esi deduction amount', esiDeduction.amount)
  //     initialGrossSalary += Math.ceil(esiDeduction.amount)
  //     const esiAmount = Math.ceil(esiDeduction.amount)

  //     // const specialAllowance = breakdown.earnings.find(comp => comp.name === 'Special Allowances')

  //     // if (specialAllowance) {
  //     //   specialAllowance.amount += esiAmount
  //     // } else {
  //     //   breakdown.earnings.push({
  //     //     name: 'Special Allowances',
  //     //     amount: esiAmount
  //     //   })
  //     // }
  //   }
  //   // console.log('gross', breakdown.gross)
  //   // console.log('initial gross', initialGrossSalary)
  //   return {
  //     // Total CTC style gross
  //     grossSalary: Math.max(
  //       0,
  //       Math.floor(
  //         (!isProrationCase &&
  //         !previousSalary &&
  //         currentSalary.payoutFrom === payrollMonthYearStr &&
  //         currentSalaryEffectiveFromObj > firstDayOfCurrentPayPeriodMonth &&
  //         currentSalaryEffectiveFromObj <= payPeriodEndDateObj
  //           ? initialGrossSalary
  //           : breakdown.gross) ?? 0
  //       )
  //     ),

  //     calculationBreakdown: breakdown,

  //     // Employee payable
  //     netSalary: Math.max(0, netSalary)
  //   }
  // }

  //13 aug 2026 gps for off boarding
  public calculateGrossMetrics(
    ctx: PayrollCalculationContext,
    employeeJoiningDate: string | Date | null,
    effectiveLastDay: string | Date | null,
    salaryHoldFroms: string | Date | null | undefined,
    salaryHoldTos: string | Date | null | undefined,
    salaryHold: boolean
  ): {
    grossSalary: number
    calculationBreakdown: PayrollCalculationBreakdown
    netSalary: number
  } {
    const {
      currentSalary,
      previousSalary,
      payPeriodStart,
      payPeriodEnd,
      lopDaysForArrearsPeriod = 0,
      bonus = 0,
      expenses = 0,
      adjustments = 0,
      tdsAfterCess = 0,
      salaryHoldFrom,
      salaryHoldTo
    } = ctx

    // ===========================================================================
    // HELPERS
    // ===========================================================================

    const DAY_MS = 24 * 60 * 60 * 1000

    const emptyBreakdown = (): PayrollCalculationBreakdown => ({
      earnings: [],
      deductions: []
    })

    const toUTCDate = (value: string | Date | null | undefined): Date | null => {
      if (!value) {
        return null
      }

      if (value instanceof Date) {
        const date = new Date(
          Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
        )

        return Number.isNaN(date.getTime()) ? null : date
      }

      const dateString = String(value).slice(0, 10)

      const date = new Date(`${dateString}T00:00:00Z`)

      return Number.isNaN(date.getTime()) ? null : date
    }

    const addDays = (date: Date, days: number): Date => {
      return new Date(date.getTime() + days * DAY_MS)
    }

    // const minDate = (a: Date, b: Date): Date => {
    //   return a <= b ? a : b
    // }

    // const maxDate = (a: Date, b: Date): Date => {
    //   return a >= b ? a : b
    // }

    const roundNumber = (value: number): number => {
      return this.round(Number.isFinite(value) ? value : 0)
    }

    // ===========================================================================
    // BASIC DATES
    // ===========================================================================

    const startDate = toUTCDate(payPeriodStart)
    const endDate = toUTCDate(payPeriodEnd)

    if (!startDate || !endDate) {
      throw new Error('Invalid payroll pay period.')
    }

    if (endDate < startDate) {
      throw new Error('Pay period end date cannot be before start date.')
    }

    const joiningDate = toUTCDate(employeeJoiningDate)

    const salaryEffectiveFrom = toUTCDate(currentSalary?.effectiveFrom)

    const holdFrom = toUTCDate(salaryHoldFrom ?? salaryHoldFroms ?? null)

    const holdTo = toUTCDate(salaryHoldTo ?? salaryHoldTos ?? null)

    const lastWorkingDay = toUTCDate(effectiveLastDay)

    const daysInPayPeriod = getDaysInPeriod(startDate, endDate)

    const monthStart = getFirstDayOfUTCMonth(startDate)

    const monthEnd = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + 1, 0))

    const daysInMonth = getDaysInPeriod(monthStart, monthEnd)

    const payrollMonthYear = getYearMonthString(startDate)

    // ===========================================================================
    // 1. PAYABLE START DATE
    // ===========================================================================

    let payableStartDate = startDate

    if (joiningDate && joiningDate > payableStartDate) {
      payableStartDate = joiningDate
    }

    // Employee joined after payroll period
    if (payableStartDate > endDate) {
      return {
        grossSalary: 0,
        calculationBreakdown: emptyBreakdown(),
        netSalary: 0
      }
    }

    // ===========================================================================
    // 2. PAYABLE END DATE
    // ===========================================================================

    let payableEndDate = endDate

    /**
     * Salary Hold:
     *
     * holdFrom = 10 May
     *
     * Payable:
     * 1 May -> 9 May
     *
     * 10 May onwards = held
     */
    if (salaryHold && holdFrom) {
      if (holdFrom <= payableEndDate) {
        payableEndDate = addDays(holdFrom, -1)
      }
    } else if (lastWorkingDay && lastWorkingDay < payableEndDate) {
      /**
       * Normal resignation:
       *
       * Last working day is payable.
       *
       * LWD = 10 May
       *
       * Payable:
       * 1 May -> 10 May
       */
      payableEndDate = lastWorkingDay
    }

    // ===========================================================================
    // 3. WORKED DAYS
    // ===========================================================================

    let workedDays = 0

    if (payableEndDate >= payableStartDate) {
      workedDays = getDaysInPeriod(payableStartDate, payableEndDate)
    }

    workedDays = Math.max(0, Math.min(workedDays, daysInPayPeriod))

    // ===========================================================================
    // 4. LOP
    // ===========================================================================

    /**
     * NEVER mutate ctx.lopDays.
     */
    let lopDays = Math.max(0, Number(ctx.lopDays) || 0)

    /**
     * Joining after month start.
     *
     * Example:
     *
     * Joining = 10 Jan
     *
     * 1 Jan -> 9 Jan = unpaid
     *
     * Therefore add 9 days LOP.
     */
    if (joiningDate && joiningDate > monthStart && joiningDate <= endDate) {
      const preJoiningDays = getDaysInPeriod(monthStart, addDays(joiningDate, -1))

      lopDays += Math.max(0, preJoiningDays)
    }

    /**
     * Salary effective date after month start.
     *
     * Example:
     *
     * Effective = 15 Jan
     *
     * 1 Jan -> 14 Jan = not payable.
     */
    if (salaryEffectiveFrom && salaryEffectiveFrom > monthStart && salaryEffectiveFrom <= endDate) {
      const preEffectiveDays = getDaysInPeriod(monthStart, addDays(salaryEffectiveFrom, -1))

      lopDays += Math.max(0, preEffectiveDays)
    }

    /**
     * Don't allow LOP above pay-period days.
     */
    lopDays = Math.min(lopDays, daysInPayPeriod)

    // ===========================================================================
    // 5. PAYABLE DAYS AFTER LOP
    // ===========================================================================

    const payableDaysAfterLop = Math.max(0, Math.min(workedDays - lopDays, daysInPayPeriod))

    // ===========================================================================
    // 6. PRORATION FACTOR
    // ===========================================================================

    const lopProrationFactor =
      daysInPayPeriod > 0 ? Math.max(0, Math.min(1, payableDaysAfterLop / daysInPayPeriod)) : 0

    // ===========================================================================
    // 7. SALARY CASES
    // ===========================================================================

    const isArrearsCase =
      !!previousSalary &&
      currentSalary.payoutFrom === payrollMonthYear &&
      !!salaryEffectiveFrom &&
      salaryEffectiveFrom < monthStart

    const isProrationCase =
      !!previousSalary &&
      currentSalary.payoutFrom === payrollMonthYear &&
      !!salaryEffectiveFrom &&
      salaryEffectiveFrom > monthStart &&
      salaryEffectiveFrom <= endDate

    const isNewSalaryProration =
      !previousSalary &&
      currentSalary.payoutFrom === payrollMonthYear &&
      !!salaryEffectiveFrom &&
      salaryEffectiveFrom > monthStart &&
      salaryEffectiveFrom <= endDate

    // ===========================================================================
    // 8. CTC / EMPLOYER CONTRIBUTION
    // ===========================================================================

    const otherComponents = currentSalary.salaryData?.otherComponents ?? []

    let employerPercentageInCTC = 0

    for (const component of otherComponents) {
      if (component.componentType !== 'customDeductions') {
        continue
      }

      const info = component.info ?? {}

      if (info.isInCTC === true && info.calculationType === 'percentage') {
        employerPercentageInCTC += Number(info.employerContribution || 0)
      }
    }

    const employerMultiplier = 1 + employerPercentageInCTC / 100

    // ===========================================================================
    // 9. MONTHLY EMPLOYEE GROSS
    // ===========================================================================

    /**
     * IMPORTANT:
     *
     * If ctcValue is annual CTC:
     *
     *     monthly CTC = ctcValue / 12
     *
     * Then remove employer contribution:
     *
     *     employee monthly gross =
     *       monthly CTC / employerMultiplier
     *
     * Example:
     *
     * CTC = 100000
     * employer % = 20%
     *
     * monthly employee gross =
     *
     * 100000 / 12 / 1.20
     */
    const annualCTC = Number(currentSalary.ctcValue || 0)

    const monthlyCTC = annualCTC > 0 ? annualCTC / 12 : 0

    const monthlyEmployeeGross = roundNumber(monthlyCTC / employerMultiplier)
    // ===========================================================================
    // 10. BUILD BREAKDOWN
    // ===========================================================================

    let calculatedArrearsAmount = 0

    let breakdown: PayrollCalculationBreakdown = emptyBreakdown()

    if (isArrearsCase && previousSalary) {
      calculatedArrearsAmount = this.calculateArrears(
        currentSalary,
        previousSalary,
        monthStart,
        lopDaysForArrearsPeriod
      )

      breakdown = this.calculateMonthlySalaryComponents(
        currentSalary,
        lopProrationFactor,
        payPeriodStart,
        payPeriodEnd,
        ctx
      )
    } else if (isProrationCase && previousSalary) {
      breakdown = this.calculateProratedSalary(
        currentSalary,
        previousSalary,
        startDate,
        endDate,
        lopProrationFactor
      )
    } else {
      breakdown = this.calculateMonthlySalaryComponents(
        currentSalary,
        lopProrationFactor,
        payPeriodStart,
        payPeriodEnd,
        ctx
      )
    }

    // ===========================================================================
    // 11. CONTEXTUAL EARNINGS
    // ===========================================================================

    if (calculatedArrearsAmount > 0) {
      breakdown.earnings.push({
        name: 'Arrears',
        amount: roundNumber(calculatedArrearsAmount)
      })
    }

    if (bonus > 0) {
      breakdown.earnings.push({
        name: 'Bonus',
        amount: roundNumber(bonus)
      })
    }

    if (expenses > 0) {
      breakdown.earnings.push({
        name: 'Expenses Reimbursed',
        amount: roundNumber(expenses)
      })
    }

    if (adjustments !== 0) {
      if (adjustments > 0) {
        breakdown.earnings.push({
          name: 'Other Adjustments',
          amount: roundNumber(adjustments)
        })
      } else {
        breakdown.deductions.push({
          name: 'Other Adjustments',
          amount: roundNumber(Math.abs(adjustments))
        })
      }
    }

    if (ctx.rounding !== 0) {
      if (ctx.rounding > 0) {
        breakdown.earnings.push({
          name: 'Rounding Adjustment',
          amount: roundNumber(ctx.rounding)
        })
      } else {
        breakdown.deductions.push({
          name: 'Rounding Adjustment',
          amount: roundNumber(Math.abs(ctx.rounding))
        })
      }
    }

    // ===========================================================================
    // 12. BONUS SPECIAL ALLOWANCE
    // ===========================================================================

    if (bonus > 0 && lopDays === 0) {
      const otherAllowances = roundNumber(
        breakdown.earnings
          .filter(component => {
            const name = component.name?.toLowerCase()

            return !['basic', 'hra', 'special allowances'].includes(name)
          })
          .reduce((sum, component) => sum + Number(component.amount || 0), 0)
      )

      const specialAllowance = breakdown.earnings.find(
        component => component.name?.toLowerCase() === 'special allowances'
      )

      if (specialAllowance) {
        specialAllowance.amount = Math.max(
          0,
          Math.floor(Math.ceil(specialAllowance.amount) - Math.ceil(otherAllowances) + bonus - 1)
        )
      }
    }

    // ===========================================================================
    // 13. NORMAL MONTHLY GROSS
    // ===========================================================================

    /**
     * THIS IS THE IMPORTANT FIX.
     *
     * Don't use breakdown.gross as the source of truth
     * for normal employee gross.
     *
     * Example:
     *
     * Monthly gross = 8333
     * Worked = 11
     * Days = 31
     *
     * Gross =
     *
     * 8333 * 11 / 31
     *
     * = 2957 approx.
     */
    let employeeGross = roundNumber(monthlyEmployeeGross * lopProrationFactor)

    // ===========================================================================
    // 14. SPECIAL SALARY CASES
    // ===========================================================================

    /**
     * For arrears/proration where previous salary exists,
     * use the salary-calculation engine's result.
     */
    if (isArrearsCase || isProrationCase) {
      const breakdownGross = Number(breakdown.gross || 0)

      if (breakdownGross > 0) {
        employeeGross = roundNumber(breakdownGross / employerMultiplier)
      }
    }

    /**
     * New salary effective in current month:
     *
     * Monthly gross is prorated based on payable days.
     *
     * Don't use raw breakdown.gross.
     */
    if (isNewSalaryProration) {
      employeeGross = roundNumber(monthlyEmployeeGross * lopProrationFactor)
    }

    // ===========================================================================
    // 15. ADD BONUS / ARREARS / EXPENSES / ADJUSTMENTS
    // ===========================================================================

    /**
     * These are already present in breakdown.
     *
     * So employeeGross should include:
     *
     * Base gross
     * + Bonus
     * + Arrears
     * + Expenses
     * + Positive adjustments
     * - Negative adjustments
     *
     * We calculate contextual amount separately to avoid
     * double counting salary components.
     */
    const contextualEarnings = breakdown.earnings
      .filter(component => {
        const name = component.name?.toLowerCase()

        return [
          'arrears',
          'bonus',
          'expenses reimbursed',
          'other adjustments',
          'rounding adjustment'
        ].includes(name)
      })
      .reduce((sum, component) => sum + Number(component.amount || 0), 0)

    const contextualDeductions = breakdown.deductions
      .filter(component => {
        const name = component.name?.toLowerCase()

        return ['other adjustments', 'rounding adjustment'].includes(name)
      })
      .reduce((sum, component) => sum + Number(component.amount || 0), 0)

    /**
     * Don't add contextual values again if breakdown.gross
     * already came from salary engine for arrears/proration.
     *
     * For normal calculation, base employeeGross is independent
     * of breakdown contextual earnings.
     */
    if (!isArrearsCase && !isProrationCase) {
      employeeGross = roundNumber(employeeGross + contextualEarnings - contextualDeductions)
    }

    // ===========================================================================
    // 16. ESI ELIGIBLE GROSS
    // ===========================================================================

    /**
     * ESI should be based on employee gross,
     * excluding reimbursements / rounding / adjustments.
     *
     * Add bonus/arrears if they are present.
     */
    const esiExcludedNames = new Set([
      'expenses reimbursed',
      'rounding adjustment',
      'other adjustments'
    ])

    const breakdownEsiAmount = breakdown.earnings
      .filter(component => {
        const name = component.name?.toLowerCase()

        return !esiExcludedNames.has(name)
      })
      .reduce((sum, component) => sum + Number(component.amount || 0), 0)

    /**
     * Use the larger reliable base:
     *
     * - employeeGross
     * - breakdown ESI base
     *
     * But don't allow statutory base to exceed actual employee gross
     * for normal payroll.
     */
    const esiEligibleGross = roundNumber(
      Math.max(
        0,
        Math.min(
          employeeGross,
          breakdownEsiAmount > 0 ? breakdownEsiAmount / employerMultiplier : employeeGross
        )
      )
    )

    // ===========================================================================
    // 17. PF ELIGIBLE GROSS
    // ===========================================================================

    let pfEligibleGross = roundNumber(
      breakdown.earnings
        .filter(component => ['Basic', 'DA'].includes(component.name))
        .reduce((sum, component) => sum + Number(component.amount || 0), 0)
    )

    /**
     * Don't let PF base exceed employee gross.
     */
    pfEligibleGross = Math.min(pfEligibleGross, employeeGross)

    // ===========================================================================
    // 18. ESI ELIGIBILITY
    // ===========================================================================

    const esiStartDate = new Date(Date.UTC(2025, 10, 1))

    const esiStatutoryEnabled = currentSalary.statutoryCompliances?.esi === true

    const esiEligible =
      esiStatutoryEnabled && esiEligibleGross <= 21000 && startDate >= esiStartDate

    // ===========================================================================
    // 19. STATUTORY DEDUCTIONS
    // ===========================================================================

    for (const component of otherComponents) {
      if (component.componentType !== 'customDeductions') {
        continue
      }

      const info = component.info ?? {}

      const componentName = component.name?.trim() ?? ''

      const componentNameLower = componentName.toLowerCase()

      let calculationBase = employeeGross

      // -------------------------------------------------------------------------
      // ESI
      // -------------------------------------------------------------------------

      if (componentNameLower.startsWith('esi')) {
        if (!esiEligible) {
          continue
        }

        calculationBase = esiEligibleGross
      }

      // -------------------------------------------------------------------------
      // PF
      // -------------------------------------------------------------------------

      if (componentName === 'PF' || componentName === 'Provident Fund') {
        calculationBase = pfEligibleGross
      }

      // -------------------------------------------------------------------------
      // CONTRIBUTIONS
      // -------------------------------------------------------------------------

      let employeeContribution = 0
      let employerContribution = 0

      if (info.calculationType === 'percentage') {
        employeeContribution = roundNumber(
          calculationBase * (Number(info.employeeContribution || 0) / 100)
        )

        employerContribution = roundNumber(
          calculationBase * (Number(info.employerContribution || 0) / 100)
        )
      } else {
        employeeContribution = roundNumber(Number(info.employeeContribution || 0))

        employerContribution = roundNumber(Number(info.employerContribution || 0))
      }

      // -------------------------------------------------------------------------
      // ESI
      // -------------------------------------------------------------------------

      if (componentNameLower.startsWith('esi')) {
        if (employeeContribution > 0) {
          this.upsertBreakdownItem(
            breakdown.deductions,
            'ESI (Employee Contribution)',
            employeeContribution
          )
        }

        continue
      }

      // -------------------------------------------------------------------------
      // PF
      // -------------------------------------------------------------------------

      if (componentName === 'PF' || componentName === 'Provident Fund') {
        if (employeeContribution > 0) {
          breakdown.deductions.push({
            name: 'PF (Employee Contribution)',
            amount: employeeContribution
          })
        }

        /**
         * Keep employer PF here only if your UI
         * intentionally displays it in deductions.
         */
        if (employerContribution > 0) {
          breakdown.deductions.push({
            name: 'PF (Employer Contribution)',
            amount: employerContribution
          })
        }

        continue
      }

      // -------------------------------------------------------------------------
      // GENERIC CUSTOM DEDUCTION
      // -------------------------------------------------------------------------

      if (employeeContribution > 0) {
        breakdown.deductions.push({
          name: componentName,
          amount: employeeContribution
        })
      }
    }

    // ===========================================================================
    // 20. TOTAL DEDUCTIONS
    // ===========================================================================

    const finalTotalDeductions = roundNumber(
      breakdown.deductions.reduce((sum, component) => sum + Number(component.amount || 0), 0)
    )

    // ===========================================================================
    // 21. TDS
    // ===========================================================================

    const tdsDeduction = Math.max(0, Math.ceil(Number(tdsAfterCess) || 0))

    // ===========================================================================
    // 22. NET SALARY
    // ===========================================================================

    const netSalary = Math.max(0, roundNumber(employeeGross - finalTotalDeductions - tdsDeduction))

    // ===========================================================================
    // 23. FINAL GROSS
    // ===========================================================================

    /**
     * Your existing application adds employee ESI
     * into displayed gross.
     */
    const esiDeduction = breakdown.deductions.find(component =>
      component.name?.toLowerCase().startsWith('esi')
    )

    const finalGrossSalary = employeeGross

    // if (esiDeduction) {
    //   finalGrossSalary = roundNumber(finalGrossSalary + Math.ceil(esiDeduction.amount))
    // }

    // ===========================================================================
    // DEBUG
    // ===========================================================================

    // console.log('PAYROLL DEBUG', {
    //   daysInPayPeriod,
    //   daysInMonth,

    //   joiningDate,
    //   salaryEffectiveFrom,

    //   holdFrom,
    //   holdTo,
    //   salaryHold,

    //   workedDays,
    //   lopDays,
    //   payableDaysAfterLop,
    //   lopProrationFactor,

    //   annualCTC,
    //   monthlyCTC,
    //   employerPercentageInCTC,
    //   employerMultiplier,

    //   monthlyEmployeeGross,

    //   isArrearsCase,
    //   isProrationCase,
    //   isNewSalaryProration,

    //   breakdownGross: breakdown.gross,

    //   employeeGross,

    //   esiEligible,
    //   esiEligibleGross,

    //   pfEligibleGross,

    //   finalTotalDeductions,
    //   tdsDeduction,

    //   finalGrossSalary,
    //   netSalary,

    //   earnings: breakdown.earnings,
    //   deductions: breakdown.deductions
    // })

    // ===========================================================================
    // RETURN
    // ===========================================================================

    return {
      grossSalary: Math.max(0, Math.floor(finalGrossSalary)),

      calculationBreakdown: breakdown,

      netSalary
    }
  }

  private calculateArrears(
    newSalary: EmployeeSalary,
    oldSalary: EmployeeSalary,
    firstDayOfCurrentPayPeriodMonth: Date,
    lopDaysForArrearsPeriod: number
  ): number {
    const arrearPeriodStartObj = new Date(newSalary.effectiveFrom + 'T00:00:00Z')
    const arrearPeriodEndObj = new Date(firstDayOfCurrentPayPeriodMonth.getTime())
    arrearPeriodEndObj.setUTCDate(arrearPeriodEndObj.getUTCDate() - 1)
    if (arrearPeriodStartObj > arrearPeriodEndObj) return 0

    const totalDaysInArrearPeriod = getDaysInPeriod(arrearPeriodStartObj, arrearPeriodEndObj)
    const payableDays = Math.max(0, totalDaysInArrearPeriod - lopDaysForArrearsPeriod)
    if (payableDays <= 0) return 0

    const dailyDifference = newSalary.ctcValue / 365 - oldSalary.ctcValue / 365
    return this.round(payableDays * dailyDifference)
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // PRORATED SALARY (mid-month salary revision)
  // ─────────────────────────────────────────────────────────────────────────────
  private calculateProratedSalary(
    newSalary: EmployeeSalary,
    oldSalary: EmployeeSalary,
    payPeriodStart: Date,
    payPeriodEnd: Date,
    lopProrationFactor: number
  ): PayrollCalculationBreakdown {
    const newSalaryEffectiveDate = new Date(newSalary.effectiveFrom + 'T00:00:00Z')
    const totalDaysInPayPeriod = getDaysInPeriod(payPeriodStart, payPeriodEnd)

    const daysWithOldSalary = getDaysInPeriod(
      payPeriodStart,
      new Date(newSalaryEffectiveDate.getTime() - 24 * 60 * 60 * 1000)
    )
    const daysWithNewSalary = getDaysInPeriod(newSalaryEffectiveDate, payPeriodEnd)

    const oldPartBreakdown = this.calculateProratedPortionForPeriod(
      oldSalary,
      daysWithOldSalary,
      totalDaysInPayPeriod,
      payPeriodStart.toISOString().split('T')[0],
      payPeriodEnd.toISOString().split('T')[0]
    )

    const newPartBreakdown = this.calculateProratedPortionForPeriod(
      newSalary,
      daysWithNewSalary,
      totalDaysInPayPeriod,
      payPeriodStart.toISOString().split('T')[0],
      payPeriodEnd.toISOString().split('T')[0]
    )

    const mergedBreakdown = this.mergeBreakdowns(oldPartBreakdown, newPartBreakdown)

    // Apply LOP factor to every merged component
    mergedBreakdown.earnings.forEach(
      comp => (comp.amount = this.round(comp.amount * lopProrationFactor))
    )
    mergedBreakdown.deductions.forEach(
      comp => (comp.amount = this.round(comp.amount * lopProrationFactor))
    )

    return mergedBreakdown
  }

  private calculateProratedPortionForPeriod(
    salary: EmployeeSalary,
    activeDays: number,
    totalDays: number,
    payPeriodStartStr: string,
    payPeriodEndStr: string
  ): PayrollCalculationBreakdown {
    if (totalDays <= 0) return { earnings: [], deductions: [] }

    const fullMonthBreakdown = this.calculateMonthlySalaryComponents(
      salary,
      1, // LOP applied later after merge
      payPeriodStartStr,
      payPeriodEndStr
    )

    fullMonthBreakdown.earnings.forEach(
      comp => (comp.amount = this.round((comp.amount / totalDays) * activeDays))
    )
    fullMonthBreakdown.deductions.forEach(
      comp => (comp.amount = this.round((comp.amount / totalDays) * activeDays))
    )

    return fullMonthBreakdown
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // MONTHLY SALARY COMPONENTS
  // ─────────────────────────────────────────────────────────────────────────────
  //before 1 july 2026
  // private calculateMonthlySalaryComponents(
  //   salary: EmployeeSalary,
  //   lopProrationFactor: number,
  //   payPeriodStartStr: string,
  //   payPeriodEndStr: string,
  //   ctx?: any
  // ): PayrollCalculationBreakdown {
  //   const earnings: SalaryComponent[] = []
  //   const deductions: SalaryComponent[] = []
  //   const { salaryData, statutoryCompliances, effectiveFrom } = salary

  //   const prorate = (amount: number) =>
  //     this.prorateComponentForEffectiveness(
  //       amount,
  //       effectiveFrom,
  //       payPeriodStartStr,
  //       payPeriodEndStr
  //     )

  //   const addEarning = (name: string, annualAmount: number) => {
  //     const amount =
  //       name === 'Basic'
  //         ? Math.round(prorate(annualAmount / 12) * lopProrationFactor)
  //         : Math.floor(prorate(annualAmount / 12) * lopProrationFactor)

  //     if (amount > 0) earnings.push({ name, amount })
  //   }

  //   const addDeduction = (name: string, annualAmount: number) => {
  //     const amount = this.round((annualAmount / 12) * lopProrationFactor)
  //     if (amount > 0) deductions.push({ name, amount })
  //   }

  //   // Standard components
  //   addEarning('Basic', salaryData.basic)
  //   addEarning('HRA', salaryData.hra)
  //   addEarning('Special Allowances', salaryData.specialAllowance)

  //   if (statutoryCompliances.pf) {
  //     addDeduction("Employee's PF Contribution", salaryData.pf)
  //   }
  //   let shouldRecalculateSpecialAllowance = false
  //   let finalGross = salary.ctcValue / 12 + ctx.bonus + ctx.expenses + ctx.adjustments
  //   // console.log('final gross 1032', finalGross)
  //   // Dynamic / other components
  //   salaryData.otherComponents?.forEach((comp: ExtendedSalaryComponent) => {
  //     const isInCTC = comp.info?.isInCTC === true
  //     const isCustomDeduction = comp.componentType === 'customDeductions'
  //     const componentName = comp.name
  //     // console.log('lop proration factor', lopProrationFactor)
  //     // ── Employer contribution ────────────────────────────────────────────
  //     // Only add employer contribution to EARNINGS when:
  //     //   1. It is part of CTC (isInCTC = true)
  //     //   2. It is NOT a customDeduction type (e.g. ESI, PF as custom)
  //     //
  //     // WHY exclude customDeductions here?
  //     //   customDeduction employer contributions (e.g. ESI employer share) are
  //     //   STATUTORY costs that sit outside the employee's take-home gross.
  //     //   If we push them to earnings here, they inflate grossSalary.
  //     //   Example: 10,000 gross + 750 ESI employer (isInCTC=true) = 10,750 gross — WRONG.
  //     //   These are handled separately in calculateGrossMetrics as employer costs.
  //     if (comp.employerContribution && isInCTC && !isCustomDeduction) {
  //       const amount = Math.floor(prorate(comp.employerContribution / 12) * lopProrationFactor)
  //       if (amount > 0) earnings.push({ name: componentName, amount })
  //     }

  //     // ── Employee contribution ────────────────────────────────────────────
  //     // customDeductions employee contributions are handled in calculateGrossMetrics
  //     // after finalGrossSalary is computed — to avoid double-counting here.
  //     if (comp.employeeContribution && !isCustomDeduction) {
  //       const amount = isInCTC
  //         ? prorate(comp.employeeContribution / 12) * lopProrationFactor
  //         : comp.employeeContribution
  //       // if (amount > 0) deductions.push({ name: `${componentName} (Contribution)`, amount })
  //       if (amount > 0) {
  //         this.upsert(deductions, `${componentName} (Contribution)`, amount)
  //       }
  //     }
  //     // let esiContribution = 0
  //     let esiContribution = earnings.find(e => e.name === 'Special Allowances')?.amount || 0
  //     if (
  //       comp.info.employeeContribution &&
  //       isInCTC &&
  //       isCustomDeduction &&
  //       componentName.trim().toLowerCase().startsWith('esi')
  //     ) {
  //       shouldRecalculateSpecialAllowance = true
  //       // const gross =
  //       //   Number(salaryData.basic || 0) +
  //       //   Number(salaryData.hra || 0) +
  //       //   Number(salaryData.specialAllowance || 0) +
  //       //   Number(comp.employeeContribution || 0) +
  //       //   Number(comp.employerContribution || 0)
  //       // const gross = earnings.reduce((sum, earning) => {
  //       //   return sum + Number(earning.amount || 0)
  //       // }, 0)

  //       // const grossEarning = earnings.reduce((sum, earning) => {
  //       //   return sum + Number(earning.amount || 0)
  //       // }, 0)
  //       // const gross =
  //       //   grossEarning +
  //       //   (comp.employerContribution ?? 0) / 12 +
  //       //   (comp.employeeContribution ?? 0) / 12

  //       let gross =
  //         (salary.ctcValue / 12 + ctx.bonus + ctx.expenses + ctx.adjustments + ctx.rounding) /
  //         (1 + (comp.info.employerContribution ?? 0) / 100)
  //       console.log(lopProrationFactor)
  //       finalGross = gross

  //       if (lopProrationFactor < 1 && gross < 21000) {
  //         const totalBonus = earnings
  //           .filter(item => item.name?.trim().toLowerCase() === 'bonus')
  //           .reduce((sum, item) => sum + Number(item.amount || 0), 0)

  //         // const annualctcforlop = (salary.ctcValue / 12) * lopProrationFactor + (ctx?.bonus ?? 0)
  //         const annualctcforlop = (salary.ctcValue / 12) * lopProrationFactor

  //         gross =
  //           (annualctcforlop + ctx.bonus + ctx.expenses + ctx.adjustments + ctx.rounding) /
  //           (1 + (comp.info.employerContribution ?? 0) / 100)
  //         // console.log('gross', gross)
  //         finalGross = gross
  //         // const basicAmount = Math.floor(annualctcforlop * 0.5 - (ctx?.bonus ?? 0))
  //         const basicAmount = Math.floor(annualctcforlop * 0.5)
  //         const hraAmount = Math.floor(basicAmount * 0.4)
  //         console.log('final gross, lop factor less than 1', finalGross)
  //         // Update Basic
  //         this.upsert(earnings, 'Basic', basicAmount)

  //         // Update HRA
  //         this.upsert(earnings, 'HRA', hraAmount)

  //         // Optional: update Special Allowance as remaining
  //         // const specialAllowance = Math.floor(gross - basicAmount - hraAmount)

  //         // this.upsert(earnings, 'Special Allowances', specialAllowance)

  //         // esiContribution = specialAllowance
  //         // console.log('special allowances', esiContribution)
  //         // console.log('specialAllowance', specialAllowance)
  //         // this.upsert(earnings, 'Special Allowances', specialAllowance)
  //         // console.log('earnings', earnings)

  //         // const totalWithoutSpecialAllowance = earnings
  //         //   .filter(item => item.name !== 'Special Allowances')
  //         //   .reduce((sum, item) => sum + Number(item.amount || 0), 0)

  //         // const specialAllowance = Math.ceil(gross - totalWithoutSpecialAllowance)
  //         // console.log('special allownce', totalWithoutSpecialAllowance)
  //         // this.upsert(earnings, 'Special Allowances', specialAllowance)
  //       } else if (lopProrationFactor === 1 && gross < 21000 && ctx.bonus > 0) {
  //         const totalWithoutSpecialAllowance = earnings
  //           .filter(item => item.name !== 'Special Allowances')
  //           .reduce((sum, item) => sum + Number(item.amount || 0), 0)
  //         // console.log('special allownce', totalWithoutSpecialAllowance)
  //         const specialAllowance = Math.ceil(
  //           gross -
  //             totalWithoutSpecialAllowance -
  //             ctx.bonus -
  //             ctx.adjustments -
  //             ctx.rounding -
  //             ctx.expenses
  //         )
  //         this.upsert(earnings, 'Special Allowances', specialAllowance)
  //       }

  //       // const esi = (gross * ((comp.info.employeeContribution ?? 0) / 100)) / 12
  //       const esi = gross * ((comp.info.employeeContribution ?? 0) / 100)
  //       const amount = prorate(esi)
  //       // esiContribution += amount
  //       if (amount > 0) {
  //         // deductions.push({ name: `${componentName} (Employee Contribution)`, amount })
  //         this.upsert(deductions, 'ESI (Employee Contribution)', Math.ceil(amount))
  //       }
  //     } else if (
  //       comp.info.employeeContribution &&
  //       !isInCTC &&
  //       isCustomDeduction &&
  //       componentName.trim().toLowerCase().startsWith('esi')
  //     ) {
  //       shouldRecalculateSpecialAllowance = true
  //       let gross = salary.ctcValue / 12
  //       if (lopProrationFactor < 1 && gross < 21000) {
  //         const annualctcforlop = (salary.ctcValue / 12) * lopProrationFactor + (ctx?.bonus ?? 0)
  //         gross = annualctcforlop
  //         finalGross = gross
  //         // console.log('gross', gross)

  //         // 1. Basic का पूरा (exact) मान और Floor मान निकालें
  //         const exactBasic = annualctcforlop * 0.5 - (ctx?.bonus ?? 0)
  //         const basicAmount = Math.floor(exactBasic)

  //         // 2. HRA का पूरा (exact) मान और Floor मान निकालें
  //         const hraAmount = Math.floor(basicAmount * 0.4)
  //         // ध्यान दें: यदि HRA मूल रूप से exactBasic पर निर्भर है, तो आप exactBasic * 0.4 भी कर सकते हैं
  //         const exactHra = basicAmount * 0.4

  //         // 3. Basic और HRA के दशमलव (Decimal) हिस्से का योग निकालें
  //         const basicDecimal = exactBasic - basicAmount
  //         const hraDecimal = exactHra - hraAmount
  //         const totalDecimals = basicDecimal + hraDecimal
  //         // console.log(totalDecimals)
  //         // Basic और HRA को अपडेट करें
  //         this.upsert(earnings, 'Basic', basicAmount)
  //         this.upsert(earnings, 'HRA', hraAmount)

  //         // 4. Special Allowance से पहले बाकी भत्तों का योग निकालें
  //         const totalWithoutSpecialAllowance = earnings
  //           .filter(item => item.name !== 'Special Allowances' && item.name !== 'Bonus')
  //           .reduce((sum, item) => sum + Number(item.amount || 0), 0)

  //         // 5. Special Allowance की गणना करें और उसमें दशमलव का योग (totalDecimals) जोड़ें
  //         // Math.ceil का उपयोग करने से बचा हुआ अंश भी पूर्ण संख्या बन जाएगा
  //         const specialAllowance = Math.floor(
  //           gross -
  //             totalWithoutSpecialAllowance -
  //             (ctx.bonus ?? 0) -
  //             (ctx.expenses ?? 0) -
  //             (ctx.adjustments ?? 0) -
  //             (ctx.rounding ?? 0) +
  //             Math.floor(totalDecimals)
  //         )

  //         // Special Allowance को अपडेट करें
  //         this.upsert(earnings, 'Special Allowances', specialAllowance)
  //       } else if (lopProrationFactor === 1 && gross < 21000) {
  //         const annualctcforlop = (salary.ctcValue / 12) * lopProrationFactor + (ctx?.bonus ?? 0)
  //         gross = annualctcforlop
  //         finalGross = gross
  //         // console.log('gross', gross)

  //         // 1. Basic का पूरा (exact) मान और Floor मान निकालें
  //         const exactBasic = annualctcforlop * 0.5 - (ctx?.bonus ?? 0)
  //         const basicAmount = Math.floor(exactBasic)

  //         // 2. HRA का पूरा (exact) मान और Floor मान निकालें
  //         const hraAmount = Math.floor(basicAmount * 0.4)
  //         // ध्यान दें: यदि HRA मूल रूप से exactBasic पर निर्भर है, तो आप exactBasic * 0.4 भी कर सकते हैं
  //         const exactHra = basicAmount * 0.4

  //         // 3. Basic और HRA के दशमलव (Decimal) हिस्से का योग निकालें
  //         const basicDecimal = exactBasic - basicAmount
  //         const hraDecimal = exactHra - hraAmount
  //         const totalDecimals = basicDecimal + hraDecimal
  //         // console.log(totalDecimals)
  //         // Basic और HRA को अपडेट करें
  //         this.upsert(earnings, 'Basic', basicAmount)
  //         this.upsert(earnings, 'HRA', hraAmount)

  //         // 4. Special Allowance से पहले बाकी भत्तों का योग निकालें
  //         // const totalWithoutSpecialAllowance =
  //         //   earnings
  //         //     .filter(item => item.name !== 'Special Allowances' && item.name !== 'Bonus')
  //         //     .reduce((sum, item) => sum + Number(item.amount || 0), 0) + totalDecimals

  //         // // 5. Special Allowance की गणना करें और उसमें दशमलव का योग (totalDecimals) जोड़ें
  //         // // Math.ceil का उपयोग करने से बचा हुआ अंश भी पूर्ण संख्या बन जाएगा
  //         // const specialAllowance = Math.floor(
  //         //   gross - totalWithoutSpecialAllowance - (ctx.bonus ?? 0)
  //         // )

  //         // // Special Allowance को अपडेट करें
  //         // this.upsert(earnings, 'Special Allowances', specialAllowance)
  //       }

  //       // const esi = (gross * ((comp.info.employeeContribution ?? 0) / 100)) / 12
  //       const esi = gross * ((comp.info.employeeContribution ?? 0) / 100)
  //       const amount = prorate(esi)
  //       // esiContribution += amount
  //       if (amount > 0) {
  //         // deductions.push({ name: `${componentName} (Employee Contribution)`, amount })
  //         this.upsert(deductions, 'ESI (Employee Contribution)', Math.ceil(amount))
  //       }
  //     }
  //     if (
  //       comp.info.employerContribution &&
  //       isInCTC &&
  //       isCustomDeduction &&
  //       componentName.trim().toLowerCase().startsWith('esi')
  //     ) {
  //       // const gross =
  //       //   Number(salaryData.basic || 0) +
  //       //   Number(salaryData.hra || 0) +
  //       //   Number(salaryData.specialAllowance || 0) +
  //       //   Number(comp.employeeContribution || 0) +
  //       //   Number(comp.employerContribution || 0)
  //       // const grossEarning = earnings.reduce((sum, earning) => {
  //       //   return sum + Number(earning.amount || 0)
  //       // }, 0)

  //       // const gross =
  //       //   grossEarning +
  //       //   (comp.employerContribution ?? 0) / 12 +
  //       //   (comp.employeeContribution ?? 0) / 12
  //       // console.log('gross', gross, salary.employeeId)
  //       // const esi = (gross * ((comp.info.employerContribution ?? 0) / 100)) / 12
  //       let gross = salary.ctcValue / 12
  //       if (lopProrationFactor < 1 && gross < 21000) {
  //         const annualctcforlop = (salary.ctcValue / 12) * lopProrationFactor
  //         gross =
  //           (annualctcforlop + ctx.bonus + ctx.expenses + ctx.adjustments + ctx.rounding) /
  //           (1 + (comp.info.employerContribution ?? 0) / 100)
  //         // finalGross = gross
  //         // console.log('gross', gross)

  //         // const basicAmount = Math.floor(gross * 0.5)
  //         // const hraAmount = Math.floor(basicAmount * 0.4)

  //         // // Update Basic
  //         // this.upsert(earnings, 'Basic', basicAmount)

  //         // // Update HRA
  //         // this.upsert(earnings, 'HRA', hraAmount)

  //         // Optional: update Special Allowance as remaining
  //         // const specialAllowance = Math.floor(gross - basicAmount - hraAmount)
  //         // esiContribution = specialAllowance
  //         // console.log('special allowances', esiContribution)
  //         // console.log('specialAllowance', specialAllowance)
  //         // this.upsert(earnings, 'Special Allowances', specialAllowance)
  //       }
  //       const esi = gross * ((comp.info.employerContribution ?? 0) / 100)
  //       const amount = prorate(esi)
  //       // esiContribution += amount
  //       // console.log('esi contribution', esiContribution)

  //       if (amount > 0) {
  //         // deductions.push({ name: `${componentName} (Employer Contribution)`, amount })
  //         // this.upsert(deductions, 'ESI (Employer Contribution)', amount)
  //       }
  //       // this.upsert(earnings, 'Special Allowances', Math.floor(esiContribution))
  //       // ❗ update instead of push
  //       // const saIndex = earnings.findIndex(e => e.name === 'Special Allowances')

  //       // if (saIndex !== -1) {
  //       //   earnings[saIndex] = {
  //       //     ...earnings[saIndex],
  //       //     amount: (earnings[saIndex].amount || 0) + esiContribution
  //       //   }
  //       // } else {
  //       //   earnings.push({
  //       //     name: 'Special Allowances',
  //       //     amount: esiContribution
  //       //   })
  //       // }
  //     }

  //     // if (
  //     //   comp.employeeContribution &&
  //     //   !isInCTC &&
  //     //   isCustomDeduction &&
  //     //   componentName === 'ESI Old'
  //     // ) {
  //     //   // const gross =
  //     //   //   Number(salaryData.basic || 0) +
  //     //   //   Number(salaryData.hra || 0) +
  //     //   //   Number(salaryData.specialAllowance || 0) +
  //     //   //   Number(comp.employeeContribution || 0) / 12

  //     //   const esi = (comp.employerContribution ?? 0) / 12
  //     //   const amount = prorate(esi)
  //     //   esiContribution += amount
  //     //   // if (amount > 0) {
  //     //   //   deductions.push({ name: `${componentName} (Employee Contribution)`, amount })
  //     //   // }
  //     //   // if (comp.employerContribution && comp.employerContribution > 0) {
  //     //   //   esiContribution += (comp.employeeContribution ?? 0) / 12
  //     //   // }
  //     //   // ❗ update instead of push
  //     //   const saIndex = earnings.findIndex(e => e.name === 'Special Allowances')

  //     //   if (saIndex !== -1) {
  //     //     earnings[saIndex] = {
  //     //       ...earnings[saIndex],
  //     //       amount: (earnings[saIndex].amount || 0) + esiContribution
  //     //     }
  //     //   } else {
  //     //     earnings.push({
  //     //       name: 'Special Allowances',
  //     //       amount: esiContribution
  //     //     })
  //     //   }
  //     // }
  //   })
  //   if (shouldRecalculateSpecialAllowance && lopProrationFactor < 1) {
  //     const totalWithoutSpecialAllowance = earnings
  //       .filter(item => item.name !== 'Special Allowances' && item.name !== 'Bonus')
  //       .reduce((sum, item) => sum + Number(item.amount || 0), 0)
  //     // console.log('final gross', finalGross)
  //     // console.log('totalWithoutSpecialAllowance', totalWithoutSpecialAllowance)
  //     const specialAllowance = Math.floor(
  //       Math.round(finalGross) -
  //         totalWithoutSpecialAllowance -
  //         ctx.bonus -
  //         ctx.expenses -
  //         ctx.adjustments -
  //         ctx.rounding
  //     )

  //     this.upsert(earnings, 'Special Allowances', specialAllowance)
  //   }
  //   // Round all amounts
  //   earnings.forEach(e => (e.amount = parseFloat(e.amount.toFixed(2))))
  //   deductions.forEach(d => (d.amount = parseFloat(d.amount.toFixed(2))))

  //   return { earnings, deductions, gross: finalGross }
  // }

  //1 july 2026 gps
  private calculateMonthlySalaryComponents(
    salary: EmployeeSalary,
    lopProrationFactor: number,
    payPeriodStartStr: string,
    payPeriodEndStr: string,
    ctx?: any
  ): PayrollCalculationBreakdown {
    const earnings: SalaryComponent[] = []
    const deductions: SalaryComponent[] = []

    const { salaryData, statutoryCompliances, effectiveFrom } = salary

    const prorate = (amount: number) =>
      this.prorateComponentForEffectiveness(
        amount,
        effectiveFrom,
        payPeriodStartStr,
        payPeriodEndStr
      )

    const addEarning = (name: string, annualAmount: number) => {
      const amount =
        name === 'Basic'
          ? Math.round(prorate(annualAmount / 12) * lopProrationFactor)
          : Math.floor(prorate(annualAmount / 12) * lopProrationFactor)

      if (amount > 0) {
        earnings.push({ name, amount })
      }
    }

    const addDeduction = (name: string, annualAmount: number) => {
      const amount = this.round((annualAmount / 12) * lopProrationFactor)
      if (amount > 0) {
        deductions.push({ name, amount })
      }
    }

    // Standard components
    addEarning('Basic', salaryData.basic)
    addEarning('HRA', salaryData.hra)
    addEarning('Special Allowances', salaryData.specialAllowance)

    if (statutoryCompliances.pf) {
      addDeduction("Employee's PF Contribution", salaryData.pf)
    }

    let shouldRecalculateSpecialAllowance = false

    let finalGross =
      salary.ctcValue / 12 +
      (ctx?.bonus ?? 0) +
      (ctx?.expenses ?? 0) +
      (ctx?.adjustments ?? 0) +
      (ctx?.rounding ?? 0)
    // --------------------------------------------------
    // LOP recalculation (independent of ESI)
    // --------------------------------------------------

    if (lopProrationFactor < 1) {
      const proratedGross = (salary.salaryData.basic / 12) * lopProrationFactor

      const basicAmount = Math.floor(proratedGross * 0.5)
      const hraAmount = Math.floor(basicAmount * 0.4)

      this.upsert(earnings, 'Basic', basicAmount)
      this.upsert(earnings, 'HRA', hraAmount)

      const totalWithoutSA = earnings
        .filter(e => e.name !== 'Special Allowances')
        .reduce((sum, e) => sum + Number(e.amount || 0), 0)

      const specialAllowance = Math.floor(
        proratedGross +
          (ctx?.bonus ?? 0) +
          (ctx?.expenses ?? 0) +
          (ctx?.adjustments ?? 0) +
          (ctx?.rounding ?? 0) -
          totalWithoutSA
      )

      this.upsert(earnings, 'Special Allowances', specialAllowance)

      finalGross =
        proratedGross +
        (ctx?.bonus ?? 0) +
        (ctx?.expenses ?? 0) +
        (ctx?.adjustments ?? 0) +
        (ctx?.rounding ?? 0)
    }
    salaryData.otherComponents?.forEach((comp: ExtendedSalaryComponent) => {
      const isInCTC = comp.info?.isInCTC === true
      const isCustomDeduction = comp.componentType === 'customDeductions'
      const isCustomEarning = comp.componentType === 'customEarnings'
      const componentName = comp.name || ''

      // --------------------------------------------
      // Custom Earnings
      // --------------------------------------------
      if (isCustomEarning) {
        const amount = Math.floor(
          prorate((comp.employerContribution || 0) / 12) * lopProrationFactor
        )

        if (amount > 0) {
          earnings.push({
            name: componentName,
            amount
          })
          this.upsert(earnings, componentName, amount)
        }

        return
      }

      // --------------------------------------------
      // ESI - Employee Contribution (CTC)
      // --------------------------------------------
      if (
        comp.info?.employeeContribution &&
        isInCTC &&
        isCustomDeduction &&
        componentName.trim().toLowerCase().startsWith('esi')
      ) {
        shouldRecalculateSpecialAllowance = true

        let gross =
          (salary.ctcValue / 12 +
            (ctx?.bonus ?? 0) +
            (ctx?.expenses ?? 0) +
            (ctx?.adjustments ?? 0) +
            (ctx?.rounding ?? 0)) /
          (1 + (comp.info.employerContribution ?? 0) / 100)

        if (lopProrationFactor < 1) {
          gross = finalGross / (1 + (comp.info.employerContribution ?? 0) / 100)
        }

        finalGross = gross

        const esi = gross * ((comp.info.employeeContribution ?? 0) / 100)

        const amount = prorate(esi)

        if (amount > 0) {
          this.upsert(deductions, 'ESI (Employee Contribution)', Math.ceil(amount))
        }
      }

      // --------------------------------------------
      // ESI - Employee Contribution (NON CTC)
      // --------------------------------------------
      else if (
        comp.info?.employeeContribution &&
        !isInCTC &&
        isCustomDeduction &&
        componentName.trim().toLowerCase().startsWith('esi')
      ) {
        shouldRecalculateSpecialAllowance = true

        let gross = finalGross

        if (lopProrationFactor >= 1) {
          gross =
            salary.ctcValue / 12 +
            (ctx?.bonus ?? 0) +
            (ctx?.expenses ?? 0) +
            (ctx?.adjustments ?? 0) +
            (ctx?.rounding ?? 0)
        }

        finalGross = gross

        const esi = gross * ((comp.info.employeeContribution ?? 0) / 100)

        const amount = prorate(esi)

        if (amount > 0) {
          this.upsert(deductions, 'ESI (Employee Contribution)', Math.ceil(amount))
        }
      }
      // --------------------------------------------
      // ESI - Employer Contribution
      // --------------------------------------------
      if (
        comp.info?.employerContribution &&
        isInCTC &&
        isCustomDeduction &&
        componentName.trim().toLowerCase().startsWith('esi')
      ) {
        let gross =
          lopProrationFactor < 1
            ? finalGross
            : salary.ctcValue / 12 +
              (ctx?.bonus ?? 0) +
              (ctx?.expenses ?? 0) +
              (ctx?.adjustments ?? 0) +
              (ctx?.rounding ?? 0)

        gross = gross / (1 + (comp.info.employerContribution ?? 0) / 100)

        const esi = gross * ((comp.info.employerContribution ?? 0) / 100)

        const amount = prorate(esi)

        void amount
      }
    })

    // --------------------------------------------
    // Special Allowance final adjustment
    // --------------------------------------------
    if (shouldRecalculateSpecialAllowance) {
      const totalWithoutSA = earnings
        .filter(e => e.name !== 'Special Allowances')
        .reduce((sum, e) => sum + Number(e.amount || 0), 0)

      const specialAllowance = Math.floor(
        finalGross -
          totalWithoutSA -
          (ctx?.bonus ?? 0) -
          (ctx?.expenses ?? 0) -
          (ctx?.adjustments ?? 0) -
          (ctx?.rounding ?? 0)
      )
      // console.log('final gross', finalGross)
      // console.log('total without sa', totalWithoutSA)
      // console.log('spcial allowance 1694', specialAllowance)
      this.upsert(earnings, 'Special Allowances', Math.max(0, specialAllowance))
    }

    // --------------------------------------------
    // Final rounding
    // --------------------------------------------
    earnings.forEach(e => {
      e.amount = parseFloat(e.amount.toFixed(2))
    })

    deductions.forEach(d => {
      d.amount = parseFloat(d.amount.toFixed(2))
    })

    // --------------------------------------------
    // Return result
    // --------------------------------------------
    return {
      earnings,
      deductions,
      gross: finalGross
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // MERGE BREAKDOWNS
  // ─────────────────────────────────────────────────────────────────────────────
  private mergeBreakdowns(
    ...breakdowns: PayrollCalculationBreakdown[]
  ): PayrollCalculationBreakdown {
    const finalBreakdown: PayrollCalculationBreakdown = { earnings: [], deductions: [] }
    const earningsMap = new Map<string, number>()
    const deductionsMap = new Map<string, number>()

    for (const breakdown of breakdowns) {
      breakdown.earnings.forEach(comp =>
        earningsMap.set(comp.name, (earningsMap.get(comp.name) || 0) + comp.amount)
      )
      breakdown.deductions.forEach(comp =>
        deductionsMap.set(comp.name, (deductionsMap.get(comp.name) || 0) + comp.amount)
      )
    }

    earningsMap.forEach((amount, name) =>
      finalBreakdown.earnings.push({ name, amount: Math.floor(amount) })
    )
    deductionsMap.forEach((amount, name) =>
      finalBreakdown.deductions.push({ name, amount: this.round(amount) })
    )

    return finalBreakdown
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // PRORATE FOR EFFECTIVENESS (partial month join/revision)
  // ─────────────────────────────────────────────────────────────────────────────
  private prorateComponentForEffectiveness(
    fullMonthlyAmount: number,
    componentEffectiveFromStr: string,
    payPeriodStartStr: string,
    payPeriodEndStr: string
  ): number {
    const componentEffectiveFrom = new Date(componentEffectiveFromStr + 'T00:00:00Z')
    const payPeriodStartDate = new Date(payPeriodStartStr + 'T00:00:00Z')
    const payPeriodEndDate = new Date(payPeriodEndStr + 'T00:00:00Z')

    if (componentEffectiveFrom > payPeriodEndDate) return 0
    if (componentEffectiveFrom <= payPeriodStartDate) return fullMonthlyAmount

    const totalDaysInPayPeriod = getDaysInPeriod(payPeriodStartDate, payPeriodEndDate)
    if (totalDaysInPayPeriod <= 0) return 0

    const effectiveDaysInPeriod = getDaysInPeriod(componentEffectiveFrom, payPeriodEndDate)
    return parseFloat(
      ((fullMonthlyAmount / totalDaysInPayPeriod) * effectiveDaysInPeriod).toFixed(2)
    )
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // TDS
  // ─────────────────────────────────────────────────────────────────────────────
  private calculateTds(grossSalary: number): number {
    if (grossSalary < this.monthlyTdsThreshold) return 0
    // Placeholder: 5% flat — replace with actual slab logic
    return 0
  }
  private upsert(arr: any[], name: string, amount: number) {
    const index = arr.findIndex(x => x.name === name)

    if (index !== -1) {
      arr[index] = {
        ...arr[index],
        amount
      }
    } else {
      arr.push({ name, amount })
    }
  }

  private upsertBreakdownItem(
    items: { name: string; amount: number }[],
    name: string,
    amount: number
  ): void {
    const existingIndex = items.findIndex(x => x.name === name)

    if (existingIndex !== -1) {
      items[existingIndex] = {
        ...items[existingIndex],
        amount: this.round(amount)
      }
    } else {
      items.push({
        name,
        amount: this.round(amount)
      })
    }
  }
}
